import React from "react";
import { dateFormatter } from "../../../utils/UtilityFunctions";
import { hasScore, needsFeelingScore } from "../../../utils/dayProgress";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export const DayListPanel = ({
  days,
  selectedIndex,
  onSelect,
  compareMode,
  compareIds,
  onToggleCard,
  onClearCompare,
  onToggleCompareMode,
  search,
  onSearchChange,
  selectedCardRef,
  filterYear,
  filterMonth,
  filterWeekday,
  filterStatus,
  onFilterYear,
  onFilterMonth,
  onFilterWeekday,
  onFilterStatus,
  filterOptions,
  scoreBucket,
  viewType,
  onVisit,
  onDelete,
}) => {
  const isMobile = viewType === "mobile";

  return (
    <div className="log__list-panel">
      <div className="log__toolbar">
        <input
          className="log__search-input"
          type="search"
          placeholder="Search by date..."
          value={search}
          onChange={onSearchChange}
        />
        {!isMobile && (
          <button
            className={`log__compare-toggle${compareMode ? " active" : ""}`}
            onClick={onToggleCompareMode}
          >
            {compareMode ? "Exit" : "Compare"}
          </button>
        )}
      </div>

      {filterOptions && (
        <div className="log__filter-bar">
          {/* Status stays on mobile, where the list is longest and the preview
              panel is hidden. Four selects wrapping at 360px does not. */}
          <select
            className="log__filter-select"
            value={filterStatus}
            onChange={(e) => onFilterStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All entries</option>
            <option value="needs-score">Needs a score</option>
            <option value="complete">Complete</option>
          </select>
          {!isMobile && (
            <>
              <select
                className="log__filter-select"
                value={filterYear}
                onChange={(e) => onFilterYear(e.target.value)}
                aria-label="Filter by year"
              >
                <option value="">All Years</option>
                {filterOptions.years.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
              <select
                className="log__filter-select"
                value={filterMonth}
                onChange={(e) => onFilterMonth(e.target.value)}
                aria-label="Filter by month"
              >
                <option value="">All Months</option>
                {filterOptions.months.map((m) => (
                  <option key={m} value={String(m)}>{MONTH_NAMES[m]}</option>
                ))}
              </select>
              <select
                className="log__filter-select"
                value={filterWeekday}
                onChange={(e) => onFilterWeekday(e.target.value)}
                aria-label="Filter by weekday"
              >
                <option value="">All Days</option>
                {filterOptions.weekdays.map((w) => (
                  <option key={w} value={String(w)}>{WEEKDAY_NAMES[w]}</option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {!isMobile && compareMode && (
        <div className="log__compare-info">
          <span>
            {compareIds.length} selected{compareIds.length === 4 ? " (max)" : ""}
          </span>
          {compareIds.length > 0 && (
            <button className="log__compare-clear" onClick={onClearCompare}>
              Clear
            </button>
          )}
        </div>
      )}

      {days.length === 0 ? (
        <div className="tooltip__container">
          <h5 className="sickTooltip">No entries match your search.</h5>
        </div>
      ) : (
        days.map((day, index) => {
          const isSelected = index === selectedIndex;
          const isInCompare = compareIds.includes(day.id);

          let cardClass = "log__card";
          if (!isMobile && isSelected) cardClass += " log__card--selected";
          if (!isMobile && isInCompare) cardClass += " log__card--in-compare";

          const hasStartScore = hasScore(day.startScore);
          const hasEndScore = hasScore(day.endScore);
          const needsScore = needsFeelingScore(day);

          return (
            <div
              key={day.id}
              className={cardClass}
              ref={!isMobile && isSelected ? selectedCardRef : null}
              onClick={isMobile ? undefined : () => onSelect(index)}
            >
              {!isMobile && compareMode && (
                <input
                  type="checkbox"
                  className="log__card__checkbox"
                  checked={isInCompare}
                  onChange={() => onToggleCard(day.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <span className="log__card__date">
                {/* dateFormatter("") yields "undefined NaNth undefined NaN", which
                    is now reachable: an undated day written into before the date
                    was picked shows up here as in progress. */}
                {day.logDate
                  ? dateFormatter(day.logDate.slice(0, 10))
                  : "No date yet"}
              </span>
              {/* Only the one actionable state is marked, in the same three
                  words as the filter option so they read as one concept.
                  "Complete" is already said by the score badges, and an empty
                  record is the absence of a status rather than one of its own
                  (design.md §14b). */}
              {needsScore && (
                <span className="log__card__state">
                  <span className="log__card__state-dot" aria-hidden="true" />
                  Needs a score
                </span>
              )}
              {/* Shown on mobile too. Without these a mobile card was just a
                  date and two buttons, identical whatever the day held. */}
              <div className="log__card__scores">
                {hasStartScore && (
                  <span
                    className={`log__card__score-badge log__card__score-badge--${scoreBucket(
                      day.startScore
                    )}`}
                  >
                    {day.startScore}
                  </span>
                )}
                {hasEndScore && (
                  <span
                    className={`log__card__score-badge log__card__score-badge--${scoreBucket(
                      day.endScore
                    )}`}
                  >
                    {day.endScore}
                  </span>
                )}
              </div>
              {isMobile && (
                <div className="log__card__mobile-actions">
                  <button
                    className="log__card__mobile-btn log__card__mobile-btn--open"
                    onClick={(e) => { e.stopPropagation(); onVisit(day.id); }}
                  >
                    Open
                  </button>
                  <button
                    className="log__card__mobile-btn log__card__mobile-btn--delete"
                    onClick={(e) => { e.stopPropagation(); onDelete(day.id); }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};
